import { CompositeLayer, CompositeLayerProps, type Layer } from "@deck.gl/core"
import { IconLayer, SolidPolygonLayer, type SolidPolygonLayerProps } from "@deck.gl/layers"
import { useEffect, useMemo, useRef, useState } from "react"
import { createExternalStore, createExternalValue } from "@nova/toolkit-pure-hooks"
import { useAppSelector } from "integrations/redux/hooks"
import {
    selectWeatherEnabled,
    selectWeatherPanel,
    selectWeatherTimeline,
} from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    bboxKey,
    useSettledView,
    useSettledViewportBbox,
    type BBox,
} from "../shared/weatherViewport"
import { getApiGatewayDomain } from "@nova/fetch-api-gateway"
import { useMapBackground, type MapBackgroundViewType } from "features/map/control/useMapStyles"
import { LightningMaskExtension, type LightningMaskExtensionProps } from "./LightningMaskExtension"
import { colormapAt, type ColorStop } from "../shared/colormap"
import { publishTimelineDensity } from "../series/timelineDensity"
import { useWeatherTimelineWindow } from "../series/weatherTimelineWindow"
import type { DetailedStrikeArea, StrikeRecord, WeatherDataResponse } from "@nova/constants"

const EMPTY_AREAS: DetailedStrikeArea[] = []
const EMPTY_STRIKES: StrikeRecord[] = []

const HOUR_MS = 60 * 60 * 1000

export const STRIKE_COLOUR_STOPS: ReadonlyArray<ColorStop> = [
    { value: 5, color: [0.98, 0.8, 0.08] },
    { value: 30, color: [0.976, 0.451, 0.086] },
    { value: 60, color: [0.863, 0.149, 0.149] },
]

export const STRIKE_KA_AXIS_MAX = STRIKE_COLOUR_STOPS[STRIKE_COLOUR_STOPS.length - 1].value

export const strikeColourRgb = (peakAmpKa: number) => {
    const [r, g, b] = colormapAt(Math.abs(peakAmpKa), STRIKE_COLOUR_STOPS)
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)] as [
        number,
        number,
        number,
    ]
}

const ICON_SIZE_PX = 16

const GROUND_STRIKE_ICON_CANDIDATES = {
    plus: "/icons/lightning/ground.svg",
    yellow500Shadow: "/icons/lightning/A-Yellow-500%20with%20shadow.svg",
    yellowGradient: "/icons/lightning/A-Yellow%20Gradient.svg",
    orangeGradient: "/icons/lightning/A-Orange%20Gradient.svg",
    whiteBolt: "/icons/lightning/A-White-Bolt.svg",
} as const

export const GROUND_STRIKE_ICON_URL = GROUND_STRIKE_ICON_CANDIDATES.plus
export const FLASH_STRIKE_ICON_URL = "/icons/lightning/question.svg"

export const LIGHTNING_STRIKES_MIN_ZOOM = 6

const STRIKE_ANIMATION_DURATION_MINUTES = 30

const ICON_RASTER_PX = 48
export const FLASH_ICON_RGB_LIGHT: [number, number, number] = [0, 0, 0]
export const FLASH_ICON_RGB_DARK: [number, number, number] = [255, 255, 255]
const FLASH_ICON_MAX_OPACITY = 0.8
const GROUND_ICON_MAX_OPACITY = 1

const strikeIcon = (url: string, mask = false) => ({
    url,
    width: ICON_RASTER_PX,
    height: ICON_RASTER_PX,
    anchorX: ICON_RASTER_PX / 2,
    anchorY: ICON_RASTER_PX / 2,
    mask,
})
const GROUND_STRIKE_ICON = strikeIcon(GROUND_STRIKE_ICON_URL, true)
const FLASH_STRIKE_ICON = strikeIcon(FLASH_STRIKE_ICON_URL, true)

const OVERLAY_RGBA_LIGHT: [number, number, number, number] = [140, 140, 140, 60]
const OVERLAY_RGBA_DARK: [number, number, number, number] = [20, 20, 20, 100]
const OVERLAY_FADE_MS = 30 * 60 * 1000

const OVERLAY_WORLD_POLYGON: [number, number][] = [
    [-180, -85],
    [180, -85],
    [180, 85],
    [-180, 85],
]

type WeatherData = { strikes: StrikeRecord[]; areas: DetailedStrikeArea[] }

const fetchWeatherData = async (
    bbox: BBox,
    rangeStart: string,
    rangeEnd: string
): Promise<WeatherData> => {
    const res = await fetch(`${getApiGatewayDomain()}/weather/data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ type: "lightningStrikes", bbox, rangeStart, rangeEnd }),
    })
    if (!res.ok) throw new Error(`Weather data fetch failed: ${res.status}`)
    const json = (await res.json()) as WeatherDataResponse
    return { strikes: json.strikes, areas: json.detailedStrikeAreas }
}

const STRIKE_LOOKBEHIND_MS = 24 * HOUR_MS
const STRIKE_LOOKAHEAD_MS = 24 * HOUR_MS
const STRIKE_BLOCK_MS = 3 * HOUR_MS
const STRIKE_BACKFILL_BURST = 3
const MAX_CACHED_STRIKE_HOURS = 72
const STRIKE_REFRESH_FRONTIER_MS = HOUR_MS
const STRIKE_HOUR_TTL_MS = 5 * 60 * 1000

const strikeHourCache = new Map<string, StrikeRecord[]>()
const strikeHourFetchedAt = new Map<string, number>()
const strikeHourInFlight = new Set<string>()
const strikesByHour = new Map<number, StrikeRecord[]>()

const FIVE_MIN_MS = 5 * 60 * 1000
const BINS_PER_HOUR = HOUR_MS / FIVE_MIN_MS

const rebuildStrikeHour = (hour: number, sources: StrikeRecord[][]) => {
    if (sources.length === 1) {
        const recs = sources[0]
        if (recs.length === 0) strikesByHour.delete(hour)
        else strikesByHour.set(hour, recs)
        return
    }
    const byTime = new Map<number, StrikeRecord[]>()
    const merged: StrikeRecord[] = []
    for (const recs of sources) {
        for (const s of recs) {
            let bucket = byTime.get(s.timeMs)
            if (!bucket) {
                bucket = []
                byTime.set(s.timeMs, bucket)
            }
            if (bucket.some((o) => o.lat === s.lat && o.lon === s.lon)) continue
            bucket.push(s)
            merged.push(s)
        }
    }
    if (merged.length === 0) strikesByHour.delete(hour)
    else strikesByHour.set(hour, merged)
}

const cacheKeyHour = (key: string): number => Number(key.slice(key.lastIndexOf("|") + 1))
const cacheKeyBbox = (key: string): string => key.slice(0, key.lastIndexOf("|"))

type HourBins = { flash: Uint16Array; ground: Uint16Array; flashTotal: number; groundTotal: number }

const MAX_CACHED_DENSITY_BINS = 256
const densityBinCache = new Map<string, HourBins>()

const invalidateDensityBins = (cacheKey: string) => {
    const prefix = `${cacheKey}|`
    for (const k of densityBinCache.keys()) if (k.startsWith(prefix)) densityBinCache.delete(k)
}

const binStrikeHour = (recs: StrikeRecord[], hour: number, viewport: BBox) => {
    const flash = new Uint16Array(BINS_PER_HOUR)
    const ground = new Uint16Array(BINS_PER_HOUR)
    let flashTotal = 0
    let groundTotal = 0
    for (const s of recs) {
        if (s.type === "IC") continue
        if (
            s.lat < viewport.south ||
            s.lat > viewport.north ||
            s.lon < viewport.west ||
            s.lon > viewport.east
        )
            continue
        const idx = Math.floor((s.timeMs - hour) / FIVE_MIN_MS)
        if (idx < 0 || idx >= BINS_PER_HOUR) continue
        if (s.type === "flash") {
            if (flash[idx] < 0xffff) flash[idx]++
            flashTotal++
        } else {
            if (ground[idx] < 0xffff) ground[idx]++
            groundTotal++
        }
    }
    return { flash, ground, flashTotal, groundTotal }
}

const publishLightningDensity = (
    bkey: string,
    viewport: BBox,
    flashStrikes: boolean,
    groundStrikes: boolean
) => {
    const viewportKey = bboxKey(viewport)
    const populated: Array<[number, HourBins]> = []
    let originMs = Infinity
    let latestHour = -Infinity
    for (const [key, recs] of strikeHourCache) {
        if (cacheKeyBbox(key) !== bkey) continue
        const hour = cacheKeyHour(key)
        const binKey = `${key}|${viewportKey}`
        let bins = densityBinCache.get(binKey)
        if (!bins) {
            bins = binStrikeHour(recs, hour, viewport)
            densityBinCache.set(binKey, bins)
        }
        if ((flashStrikes ? bins.flashTotal : 0) + (groundStrikes ? bins.groundTotal : 0) === 0)
            continue
        populated.push([hour, bins])
        if (hour < originMs) originMs = hour
        if (hour > latestHour) latestHour = hour
    }
    while (densityBinCache.size > MAX_CACHED_DENSITY_BINS) {
        const oldest = densityBinCache.keys().next().value
        if (oldest === undefined) break
        densityBinCache.delete(oldest)
    }
    if (populated.length === 0) {
        publishTimelineDensity("lightning", null)
        return
    }
    const span = (latestHour - originMs) / HOUR_MS + 1
    const counts = new Uint16Array(span * BINS_PER_HOUR)
    for (const [hour, bins] of populated) {
        const base = ((hour - originMs) / HOUR_MS) * BINS_PER_HOUR
        for (let i = 0; i < BINS_PER_HOUR; i++) {
            const total = (flashStrikes ? bins.flash[i] : 0) + (groundStrikes ? bins.ground[i] : 0)
            counts[base + i] = Math.min(0xffff, total)
        }
    }
    publishTimelineDensity("lightning", { binMs: FIVE_MIN_MS, originMs, counts })
}

const strikeCacheGeneration = createExternalValue(0)

const commitStrikeHours = (entries: Array<[string, StrikeRecord[]]>, fetchedAt: number) => {
    const sourcesByHour = new Map<number, StrikeRecord[][]>()
    for (const [key, recs] of entries) {
        strikeHourCache.set(key, recs)
        strikeHourFetchedAt.set(key, fetchedAt)
        invalidateDensityBins(key)
        sourcesByHour.set(cacheKeyHour(key), [])
    }
    while (strikeHourCache.size > MAX_CACHED_STRIKE_HOURS) {
        const oldest = strikeHourCache.keys().next().value
        if (oldest === undefined) break
        invalidateDensityBins(oldest)
        sourcesByHour.set(cacheKeyHour(oldest), [])
        strikeHourCache.delete(oldest)
        strikeHourFetchedAt.delete(oldest)
    }
    for (const [key, recs] of strikeHourCache) sourcesByHour.get(cacheKeyHour(key))?.push(recs)
    for (const [hour, sources] of sourcesByHour) rebuildStrikeHour(hour, sources)
    strikeCacheGeneration.update((generation) => generation + 1)
}

const MAX_CACHED_AREA_BBOXES = 32
const areaStore = createExternalStore<DetailedStrikeArea[]>({
    maxEntries: MAX_CACHED_AREA_BBOXES,
})
const areaKey = (a: DetailedStrikeArea) => `${a.startMs}:${a.lat}:${a.lon}`

const commitAreas = (bkey: string, areas: DetailedStrikeArea[], extentStartMs: number) => {
    const merged = new Map<string, DetailedStrikeArea>()
    for (const a of [...(areaStore.getItem(bkey) ?? []), ...areas]) {
        if (a.endMs < extentStartMs) continue
        merged.set(areaKey(a), a)
    }
    areaStore.set(bkey, [...merged.values()])
    areaStore.prune([bkey])
}

const strikesLoading = createExternalValue(false)
const setStrikeHourInFlight = (key: string, inFlight: boolean) => {
    if (inFlight) strikeHourInFlight.add(key)
    else strikeHourInFlight.delete(key)
    strikesLoading.set(strikeHourInFlight.size > 0)
}

export const useLightningStrikesLoading = strikesLoading.use

const hourStart = (ms: number) => Math.floor(ms / HOUR_MS) * HOUR_MS
const blockStart = (ms: number) => Math.floor(ms / STRIKE_BLOCK_MS) * STRIKE_BLOCK_MS
const strikeCacheKey = (bkey: string, hour: number) => `${bkey}|${hour}`

const isLeadingEdgeStrikeHour = (hour: number, now: number) =>
    hour + HOUR_MS > now - STRIKE_REFRESH_FRONTIER_MS
const isStaleStrikeHour = (key: string, now: number) =>
    now - (strikeHourFetchedAt.get(key) ?? 0) > STRIKE_HOUR_TTL_MS

const useStrikes = () => {
    const weather = useAppSelector(selectWeatherEnabled)
    const { mode, groundStrikes, flashStrikes } = useAppSelector(selectWeatherPanel)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const { currentTime } = useAppSelector(selectWeatherTimeline)
    const { startMs: extentStart, endMs: windowEndMs } = useWeatherTimelineWindow("lightning")
    const [strikes, setStrikes] = useState<StrikeRecord[]>([])

    const active = weather && mode === "lightning"
    const settledView = useSettledView(active)
    const viewportBbox = useSettledViewportBbox(active)

    const bkey = settledView ? bboxKey(settledView.bbox) : null
    const playheadHour = hourStart(Date.parse(currentTime))

    const generation = strikeCacheGeneration.use()

    const [refreshTick, setRefreshTick] = useState(0)
    useEffect(() => {
        if (!active) return
        const id = setInterval(() => setRefreshTick((t) => t + 1), STRIKE_HOUR_TTL_MS)
        return () => clearInterval(id)
    }, [active])

    useEffect(() => {
        if (
            !active ||
            !workspaceId ||
            !settledView ||
            settledView.zoom < LIGHTNING_STRIKES_MIN_ZOOM ||
            bkey === null ||
            Number.isNaN(playheadHour)
        ) {
            setStrikes([])
            return
        }

        const extentEnd = Math.min(windowEndMs, Date.now())
        const winStart = Math.max(extentStart, playheadHour - STRIKE_LOOKBEHIND_MS)
        const winEnd = Math.min(extentEnd, playheadHour + STRIKE_LOOKAHEAD_MS)

        const neededHours: number[] = []
        for (let h = hourStart(winStart); h <= winEnd; h += HOUR_MS) neededHours.push(h)

        const neededHourSet = new Set(neededHours)
        const collect = (): StrikeRecord[] => {
            const out: StrikeRecord[] = []
            for (const hour of neededHourSet) {
                const recs = strikesByHour.get(hour)
                if (recs) for (const rec of recs) out.push(rec)
            }
            return out
        }

        let cancelled = false
        setStrikes(collect())

        const now = Date.now()
        const needsFetchHour = (h: number): boolean => {
            const k = strikeCacheKey(bkey, h)
            if (strikeHourInFlight.has(k)) return false
            if (!strikeHourCache.has(k)) return true

            return isLeadingEdgeStrikeHour(h, now) && isStaleStrikeHour(k, now)
        }

        const hoursByBlock = new Map<number, number[]>()
        for (const h of neededHours) {
            const bs = blockStart(h)
            const arr = hoursByBlock.get(bs)
            if (arr) arr.push(h)
            else hoursByBlock.set(bs, [h])
        }

        const targetBlock = blockStart(Math.min(playheadHour, now))
        const pendingBlocks = hoursByBlock.get(targetBlock)?.some(needsFetchHour)
            ? [targetBlock]
            : [...hoursByBlock.keys()]
                  .filter((bs) => hoursByBlock.get(bs)!.some(needsFetchHour))
                  .sort((a, b) => Math.abs(a - targetBlock) - Math.abs(b - targetBlock))
                  .slice(0, STRIKE_BACKFILL_BURST)
        if (pendingBlocks.length === 0) return () => void (cancelled = true)

        for (const bs of pendingBlocks) {
            const hours = hoursByBlock.get(bs)!.filter(needsFetchHour)
            const fetchStart = Math.min(...hours)
            const fetchEnd = Math.min(Math.max(...hours) + HOUR_MS, extentEnd)
            hours.forEach((h) => setStrikeHourInFlight(strikeCacheKey(bkey, h), true))

            fetchWeatherData(
                settledView.bbox,
                new Date(fetchStart).toISOString(),
                new Date(fetchEnd).toISOString()
            )
                .then((result) => {
                    const byHour = new Map<number, StrikeRecord[]>(hours.map((h) => [h, []]))
                    for (const s of result.strikes) byHour.get(hourStart(s.timeMs))?.push(s)
                    commitStrikeHours(
                        [...byHour].map(([h, recs]): [string, StrikeRecord[]] => [
                            strikeCacheKey(bkey, h),
                            recs,
                        ]),
                        Date.now()
                    )
                    commitAreas(bkey, result.areas, extentStart)
                })
                .catch((err) => console.error("[lightningStrikes] fetch failed:", err))
                .finally(() => {
                    hours.forEach((h) => setStrikeHourInFlight(strikeCacheKey(bkey, h), false))
                    if (!cancelled) setStrikes(collect())
                })
        }

        return () => void (cancelled = true)
    }, [
        active,
        workspaceId,
        settledView,
        bkey,
        playheadHour,
        extentStart,
        windowEndMs,
        refreshTick,
        generation,
    ])

    useEffect(() => {
        if (!active || bkey === null || !viewportBbox) {
            publishTimelineDensity("lightning", null)
            return
        }
        publishLightningDensity(bkey, viewportBbox, flashStrikes, groundStrikes)
    }, [active, bkey, viewportBbox, generation, flashStrikes, groundStrikes])

    const bbox = settledView?.bbox

    const visibleStrikes = useMemo(() => {
        if (!active || !bbox) return EMPTY_STRIKES
        return strikes.filter((s) => {
            if (s.lat < bbox.south || s.lat > bbox.north || s.lon < bbox.west || s.lon > bbox.east)
                return false
            return s.type === "flash" ? flashStrikes : s.type === "CG" ? groundStrikes : false
        })
    }, [active, bbox, strikes, flashStrikes, groundStrikes])

    const areasByBbox = areaStore.useAll()
    const areas = (active && bkey !== null && areasByBbox.get(bkey)) || EMPTY_AREAS
    return { strikes: visibleStrikes, areas }
}

const flashProgress = (strikeTimeMs: number, playheadMs: number, windowMs: number) => {
    const age = playheadMs - strikeTimeMs
    if (age < 0 || age > windowMs) return -1
    return age / windowMs
}

const strikeAlpha = (timeMs: number, playheadMs: number, windowMs: number) => {
    const p = flashProgress(timeMs, playheadMs, windowMs)
    if (p < 0) return 0
    return Math.round(255 * (1 - p) * (1 - p))
}

type StrikeCompositeProps = CompositeLayerProps & {
    iconData: StrikeRecord[]
    areas: DetailedStrikeArea[]
    showOverlay: boolean
    overlayFade: number
    playheadMs: number
    windowMs: number
    isLight: boolean
}

class LightningStrikesCompositeLayer
    extends CompositeLayer<StrikeCompositeProps>
    implements Layer<StrikeCompositeProps>
{
    static layerName = "LightningStrikesLayer"

    renderLayers() {
        const { iconData, areas, showOverlay, overlayFade, playheadMs, windowMs, isLight } =
            this.props
        const FLASH_MAX_ALPHA = Math.round(FLASH_ICON_MAX_OPACITY * 255)
        const GROUND_MAX_ALPHA = Math.round(GROUND_ICON_MAX_OPACITY * 255)
        const flashRgb = isLight ? FLASH_ICON_RGB_LIGHT : FLASH_ICON_RGB_DARK
        const iconColor = (d: StrikeRecord): [number, number, number, number] => {
            const alpha = strikeAlpha(d.timeMs, playheadMs, windowMs)
            if (d.type === "flash") {
                return [...flashRgb, Math.min(alpha, FLASH_MAX_ALPHA)]
            }
            return [...strikeColourRgb(d.peakAmpKa), Math.min(alpha, GROUND_MAX_ALPHA)]
        }
        const overlayRgba = isLight ? OVERLAY_RGBA_LIGHT : OVERLAY_RGBA_DARK
        const overlayFill: [number, number, number, number] = [
            overlayRgba[0],
            overlayRgba[1],
            overlayRgba[2],
            Math.round(overlayRgba[3] * overlayFade),
        ]
        const layers: Layer[] = []
        if (showOverlay) {
            layers.push(
                new SolidPolygonLayer({
                    id: `${this.props.id}-detailed-mask`,
                    data: [OVERLAY_WORLD_POLYGON],
                    getPolygon: (d) => d,
                    filled: true,
                    getFillColor: overlayFill,
                    updateTriggers: { getFillColor: [isLight, overlayFade] },
                    extensions: [new LightningMaskExtension()],
                    maskDisks: areas,
                } satisfies SolidPolygonLayerProps<[number, number][]> &
                    LightningMaskExtensionProps)
            )
        }
        layers.push(
            new IconLayer<StrikeRecord>({
                id: `${this.props.id}-icons`,
                data: iconData,
                getPosition: (d) => [d.lon, d.lat],
                getIcon: (d) => (d.type === "flash" ? FLASH_STRIKE_ICON : GROUND_STRIKE_ICON),
                sizeUnits: "pixels",
                billboard: true,
                getSize: ICON_SIZE_PX,
                getColor: iconColor,
                pickable: true,
                updateTriggers: { getColor: [playheadMs, windowMs, isLight] },
            })
        )
        return layers
    }
}

export const useLightningStrikesLayer = (viewType: MapBackgroundViewType) => {
    const { strikes, areas } = useStrikes()
    const loading = useLightningStrikesLoading()
    const { detailedAreas } = useAppSelector(selectWeatherPanel)
    const { currentTime, playbackSpeed } = useAppSelector(selectWeatherTimeline)
    const { isLight } = useMapBackground(viewType)
    const playheadMs = Date.parse(currentTime)
    const windowMs = STRIKE_ANIMATION_DURATION_MINUTES * 60 * 1000 * Math.max(0.01, playbackSpeed)

    const iconData = useMemo(() => strikes.filter((d) => d.type !== "IC"), [strikes])

    const visibleAreasRef = useRef(EMPTY_AREAS)
    const nextVisibleAreas = detailedAreas
        ? areas.filter((a) => playheadMs >= a.startMs && playheadMs <= a.endMs)
        : EMPTY_AREAS
    const previousVisibleAreas = visibleAreasRef.current
    const visibleAreas =
        previousVisibleAreas.length === nextVisibleAreas.length &&
        previousVisibleAreas.every((a, i) => a === nextVisibleAreas[i])
            ? previousVisibleAreas
            : nextVisibleAreas
    visibleAreasRef.current = visibleAreas

    const overlayFade = Math.max(0, Math.min(1, 1 - (playheadMs - Date.now()) / OVERLAY_FADE_MS))

    const showOverlay =
        detailedAreas &&
        overlayFade > 0 &&
        (strikes.length > 0 || visibleAreas.length > 0 || loading)

    if ((strikes.length === 0 && !showOverlay) || Number.isNaN(playheadMs)) return { layer: null }

    const layer = new LightningStrikesCompositeLayer({
        id: "LightningStrikesLayer",
        iconData,
        areas: visibleAreas,
        showOverlay,
        overlayFade,
        isLight,
        playheadMs,
        windowMs,
    })

    return { layer }
}
